"use client";

import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Money } from "@/components/common/Money";
import { Countdown } from "@/components/common/Countdown";
import { FileText, Send, RefreshCw, Clock } from "lucide-react";

interface LineItem {
  id: string;
  alterationType: string;
  unitPrice: number;
  qty: number;
  notes?: string | null;
  orderItem?: {
    id: string;
    garmentType: string;
  } | null;
}

interface QuotePreviewProps {
  quote: {
    id: string;
    versionNo: number;
    status: string;
    subtotal: number;
    tax: number;
    deliveryFee: number;
    total: number;
    expiresAt?: string | null;
    lineItems: LineItem[];
    createdBy?: {
      name: string;
    };
    createdAt: string;
  };
  onSend?: () => void;
  onRevise?: () => void;
  sending?: boolean;
  revising?: boolean;
}

const statusColors: Record<string, string> = {
  DRAFT: "bg-gray-100 text-gray-700",
  SENT: "bg-blue-100 text-blue-700",
  VIEWED: "bg-cyan-100 text-cyan-700",
  APPROVED: "bg-green-100 text-green-700",
  REJECTED: "bg-red-100 text-red-700",
  REVISED: "bg-amber-100 text-amber-700",
  EXPIRED: "bg-slate-100 text-slate-700",
};

export function QuotePreview({
  quote,
  onSend,
  onRevise,
  sending = false,
  revising = false,
}: QuotePreviewProps) {
  const groupedByGarment = quote.lineItems.reduce((acc, line) => {
    const garment = line.orderItem?.garmentType || "General";
    if (!acc[garment]) {
      acc[garment] = [];
    }
    acc[garment].push(line);
    return acc;
  }, {} as Record<string, LineItem[]>);

  const canSend = quote.status === "DRAFT";
  const canRevise = ["SENT", "VIEWED", "REJECTED", "EXPIRED"].includes(quote.status);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Quote v{quote.versionNo}
          </CardTitle>
          <Badge className={statusColors[quote.status] || "bg-gray-100 text-gray-700"}>
            {quote.status}
          </Badge>
        </div>
        {quote.expiresAt && quote.status === "SENT" && (
          <div className="flex items-center gap-2 mt-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Expires in:</span>
            <Countdown targetDate={quote.expiresAt} showIcon={false} />
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(groupedByGarment).map(([garment, lines]) => (
          <div key={garment}>
            <h4 className="font-medium text-sm mb-2">{garment}</h4>
            <div className="space-y-2">
              {lines.map((line) => (
                <div
                  key={line.id}
                  className="flex items-center justify-between text-sm bg-muted/50 p-2 rounded"
                >
                  <div>
                    <span>{line.alterationType}</span>
                    {line.notes && (
                      <span className="text-muted-foreground ml-2">
                        ({line.notes})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-muted-foreground">x{line.qty}</span>
                    <Money amount={line.unitPrice * line.qty} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <Separator />

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <Money amount={quote.subtotal} />
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Tax</span>
            <Money amount={quote.tax} />
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Delivery Fee</span>
            <Money amount={quote.deliveryFee} />
          </div>
          <Separator />
          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <Money amount={quote.total} />
          </div>
        </div>
      </CardContent>
      {(canSend || canRevise) && (
        <CardFooter className="flex gap-2">
          {canSend && onSend && (
            <Button onClick={onSend} disabled={sending} className="flex-1">
              <Send className="h-4 w-4 mr-2" />
              {sending ? "Sending..." : "Send Quote"}
            </Button>
          )}
          {canRevise && onRevise && (
            <Button
              variant="outline"
              onClick={onRevise}
              disabled={revising}
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {revising ? "Creating..." : "Revise Quote"}
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
}
